import {
    Box,
    Typography,
    useTheme,
    Divider,
    Stack,
    IconButton
} from "@mui/material";
import { useContext } from "react";
import { styled } from "@mui/material/styles";

import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";

import InfoDataGrid from "../../components/InfoDataGrid";
import InfoBox from "../InfoBox";

import CachedSharpIcon from '@mui/icons-material/CachedSharp';
import LockResetIcon from '@mui/icons-material/LockReset';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import GroupsIcon from '@mui/icons-material/Groups';
import CommentIcon from '@mui/icons-material/Comment';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CurrencyRubleIcon from '@mui/icons-material/CurrencyRuble';
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import BuildIcon from '@mui/icons-material/Build';
import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import PermContactCalendarIcon from '@mui/icons-material/PermContactCalendar';
import NumbersIcon from '@mui/icons-material/Numbers';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';

import { tokens } from "../../theme";
import {Context} from "../../index";
import { int2ip } from "../../utils/formatIpAddress"
import { fTimestamp } from "../../utils/formatTime";
import PersonalAccount from "./PersonalAccount";
import ServiceLinks from "./ServiceLinks";
import { Link } from "react-router-dom";

const UserComponent = ({ 
    billingId, 
    handleUpdate,
    handleOpenDiagnostic, 
    handleOpenSpeedtest,
    handleOpenPayment,
    handleOpenPromisedPayment,
    handleEnableInet,
    handleOpenResetPassword,
    billingUser,
    billingUsersGroups, 
    billingUserAccounts,
    billingUserServiceLinks,
    billingUserIpServices,
    billingUserSwitchesInfo
}) => {

    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const {store} = useContext(Context);
    const user = store.user

    const columnsIpServices = [
        {
            field: 'ip',
            headerName: 'IP',
            flex: 1,
            renderCell: ({ row: { ip, service_name, comment } }) => {
                return (
                    <Box>
                        <Typography>
                            {ip}
                        </Typography>
                        <Typography>
                            { service_name }({ comment })
                        </Typography>
                    </Box>
                )
            }
        },
        { field: 'speed', headerName: 'Speed' },
        { field: 'mac', headerName: 'Mac' },
    ];

    const columnsSwitchesInfo = [
        {
            field: 'arp_entry',
            headerName: 'Arp entry',
            flex: 1,
            renderCell: ({ row: { arp_ip, arp_time , gateway} }) => {
                return (
                    <Box>
                        <Typography>
                            { int2ip(arp_ip) }
                        </Typography>
                        <Typography>
                            { int2ip(gateway) }({ arp_time })
                        </Typography>
                    </Box>
                )
            }
        },
        {
            field: 'fdb_entry',
            headerName: 'Fdb entry',
            flex: 1,
            renderCell: ({row: {mac, fdb_time} }) => {
                return (
                    <Box>
                        <Typography>
                            {mac}
                        </Typography>
                        <Typography>
                            {fdb_time}
                        </Typography>
                    </Box>
                )
            }
        },
        {
            field: 'switch',
            headerName: 'Switch',
            flex: 1,
            renderCell: ({row: {fdb_network, sw_ip, sw_port}}) => {
                return (
                    <Box><Link to={`/search/host/${fdb_network}/${sw_ip}`}>{ int2ip(sw_ip) }:{sw_port}</Link></Box>
                )
            }
        },
        {
            field: 'diagnostic',
            headerName: 'Diagnostic',
            renderCell: ({row: {fdb_network, sw_ip, sw_port, arp_network, arp_ip, gateway}}) => {
                return (
                    <Box>
                        <IconButton onClick={() => {handleOpenDiagnostic({
                            "fdb_network": fdb_network,
                            "sw_ip": sw_ip,
                            "sw_port": sw_port,
                            "arp_network": arp_network,
                            "arp_ip" : arp_ip,
                            "gateway": gateway
                        })}} aria-label="delete" size="large">
                            <SettingsOutlinedIcon/>
                        </IconButton>
                        <IconButton onClick={() => {handleOpenSpeedtest({
                            "fdb_network": fdb_network,
                            "sw_ip": sw_ip,
                            "sw_port": sw_port,
                            "arp_network": arp_network,
                            "arp_ip" : arp_ip,
                            "gateway": gateway
                        })}} aria-label="delete" size="large">
                            <NetworkCheckIcon/>
                        </IconButton>
                    </Box>
                )
            }
        },
    ];

    return (
        <Box
            sx={{
                width: "100%",
                borderRadius: 1,
                // backgroundColor: id == selectedChatId
                //     ? theme.palette.mode === "light"
                //         ? alpha(theme.palette.primary.main, 0.5)
                //         : theme.palette.primary.main
                //     : theme.palette.mode === "light"
                //         ? "#fff"
                //         : theme.palette.background.paper,
            }}
            p={2}
        >   
            <Stack spacing={0.3}>
                {/* GENERAL */}
                <Stack
                    direction="row"
                    alignItems={"center"}
                    justifyContent="space-between"
                >
                    <Typography
                        variant="h4"
                        fontWeight="bold"
                        sx={{ color: colors.grey[100] }}
                    >
                        ID: {billingUser.user_id} ({fTimestamp(billingUser.create_date)})
                    </Typography>
                    <Stack direction={"row"}>
                        
                        <IconButton onClick={() => {
                            handleUpdate()
                        }}>
                            <CachedSharpIcon />
                        </IconButton>
                    </Stack>
                </Stack>
                <Stack direction={"row"} spacing={1} p={"5px"}>
                    {billingUser.is_juridical ? <BusinessIcon /> : <PersonIcon />}
                    <Typography variant="caption2">
                        {billingUser.full_name ? billingUser.full_name : "undefined"}
                    </Typography>    
                </Stack>
                <Divider />
                <Stack
                    direction="row"
                    alignItems={"center"}
                    // justifyContent="space-between"
                >
                    <Stack>
                        <Typography variant="caption">
                            Login: {billingUser.login}
                        </Typography>
                        <Typography variant="caption">
                            Password: *******
                        </Typography>
                    </Stack>
                    {user.allowed_functions.indexOf( 'v2_billing_user_reset_password' ) !== -1 && (
                        <IconButton onClick={() => {
                            handleOpenResetPassword()
                        }}>
                            <LockResetIcon />
                        </IconButton>
                    )}
                </Stack>
                {/* END */}
            </Stack>


            <Divider />
            <Stack
                direction="row"
                spacing={{ xs: 1, sm: 2 }}
                useFlexGap
                sx={{ flexWrap: 'wrap', alignItems: "flex-end" }}
            >
                <InfoBox title={"Location"} icon={<LocationOnIcon />} info={billingUser.actual_address} />
                {billingUser.is_juridical
                ?
                    <>
                    <InfoBox title={"Tax number"} icon={<NumbersIcon />} info={billingUser.tax_number} />
                    <InfoBox title={"Legal address"} icon={<LocationOnIcon />} info={billingUser.juridical_address} />
                    </>
                :
                    billingUser.passport
                    ? 
                        <InfoBox title={"Passport"} icon={<PermContactCalendarIcon />} info={"Data entered"} />
                    :
                        <InfoBox title={"Passport"} icon={<PermContactCalendarIcon />} info={false} />
                }             
            </Stack>    
            <Divider />
            <Stack
                direction="row"
                spacing={{ xs: 1, sm: 2 }}
                useFlexGap
                sx={{ flexWrap: 'wrap', alignItems: "flex-end" }}
            >
                <InfoBox title={"E-mail"} icon={<EmailIcon />} info={billingUser.email} />
                <InfoBox title={"Mobile telephone"} icon={<PhoneIcon />} info={billingUser.mobile_telephone} />
                {billingUser.home_telephone && <InfoBox title={"Home telephone"} icon={<PhoneIcon />} info={billingUser.home_telephone} />}
                {billingUser.work_telephone && <InfoBox title={"Work telephone"} icon={<PhoneIcon />} info={billingUser.work_telephone} />}
                <Divider />
            </Stack>
            <Divider />
            {billingUser.comments && 
                <>
                    <InfoBox title={"Comments"} icon={<CommentIcon />} info={billingUser.comments} />
                    <Divider />
                </>
            }
            <Stack m={"10px"} spacing={0.5}>
                <GroupsIcon />
                {/* <Typography variant="h5">Groups:</Typography> */}
                <Stack 
                    direction="row"
                    spacing={{ xs: 1, sm: 2 }}
                    useFlexGap
                    sx={{ flexWrap: 'wrap' }}

                >
                    {billingUsersGroups.length === 0 
                        ? <Typography variant="h5">No groups</Typography>
                        : billingUsersGroups.map((el) => {
                            return <Typography 
                                    borderRadius={1}
                                    p={0.2}
                                    backgroundColor={ colors.greenAccent[600] }
                                    >
                                        {el.group_name}
                                    </Typography>;
                        })
                    }
                </Stack>
            </Stack>
            <Divider />
            <Stack m={"10px"} spacing={0.5}>
                <Accordion expanded>
                    <AccordionSummary>
                        <CurrencyRubleIcon /> 
                        <Typography color={colors.greenAccent[500]} variant="h5">
                            Personal accounts
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        {billingUserAccounts.map((el) => {
                            return  <PersonalAccount
                                        personalAccount={el}
                                        billingId={billingId}
                                        userId={billingUser.user_id}
                                        handleOpenPayment={handleOpenPayment}
                                        handleOpenPromisedPayment={handleOpenPromisedPayment}
                                        handleEnableInet={handleEnableInet}
                                    />;
                        })}
                    </AccordionDetails>
                </Accordion>
            </Stack>
            <Divider />
            <Stack m={"10px"}>
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <ElectricalServicesIcon />
                        <Typography color={colors.greenAccent[500]} variant="h5">
                            Service link
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        {billingUserServiceLinks.map((el) => {
                            return <ServiceLinks serviceLink={el} />;
                        })}
                    </AccordionDetails>
                </Accordion>
                
            </Stack>
            <Divider />
            <Stack m={"10px"}>
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <BuildIcon />
                        <Typography color={colors.greenAccent[500]} variant="h5">
                            Technical information
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <InfoDataGrid
                            columns={columnsIpServices}
                            rows={billingUserIpServices}
                        />
                        <InfoDataGrid
                            columns={columnsSwitchesInfo}
                            rows={billingUserSwitchesInfo}
                        />
                    </AccordionDetails>
                </Accordion>
            </Stack>
        </Box>
    );
};

export default UserComponent;